import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import Header from "../../../components/Header";
import { useEffect, useState } from "react";
import {
  updateStatus,
  getSettingsError,
  getSettingsStatus,
  getSettingsLoading,
  getSettingsSuccess,
  updateSetting,
} from "../../../features/settingSlice";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Loading from "../../../components/loading";
import { useLocation, useNavigate } from "react-router-dom";
import TextFieldCompnent from "../../../components/textFieldComponent";
import ImageInput from "../../../components/imageInput";
import { useRestaurant } from "../../../hooks/RestaurantContext";
import { appendImageFormFields } from "../../../global/functions/imageHelper";
import BackButton from "../../../components/backButton";
import { useTranslation } from "react-i18next";

const defaultCardMinMessage = {
  fr: "Carte bancaire à partir de {amount} {currency}",
  en: "Card from {amount} {currency}",
  ar: "البطاقة ابتداءً من {amount} {currency}",
};

const ModifySettings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const data = location.state;
  const { restaurantId } = useRestaurant();
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const [bannerPreview, setBannerPreview] = useState(null);
  const status = useSelector(getSettingsStatus);
  const error = useSelector(getSettingsError);
  const loading = useSelector(getSettingsLoading);
  const success = useSelector(getSettingsSuccess);

  const settingsSchema = yup.object().shape({
    qrCode: yup
      .string()
      .url("Le QR code doit être une URL valide")
      .required("Le QR code est requis"),
    tva: yup
      .number()
      .required("Le TVA est requis")
      .min(0, "Le TVA minimal est 0"),
    // maxExtras: yup
    //   .number()
    //   .required("Les extras sont requis")
    //   .min(1, "L'extras minimal est 1"),
    // maxDessert: yup
    //   .number()
    //   .required("Les désserts sont requis")
    //   .min(1, "Le déssert minimal est 1"),
    // maxDrink: yup
    //   .number()
    //   .required("Les boissons sont requis")
    //   .min(1, "Le boisson minimal est 1"),
    card: yup
      .string()
      .required("Le label est requis")
      .max(20, "Le label doit être de longueur maximale 20"),
    cardActive: yup.boolean(),
    cardMinAmount: yup
      .number()
      .typeError(t("auth.required"))
      .required(t("auth.required"))
      .min(0, t("auth.required")),
    cardMinMessageFr: yup.string().max(180, t("auth.required")),
    cardMinMessageEn: yup.string().max(180, t("auth.required")),
    cardMinMessageAr: yup.string().max(180, t("auth.required")),
    loyaltyEnabled: yup.boolean(),
    cash: yup
      .string()
      .required("Le label est requis")
      .max(20, "Le label doit être de longueur maximale 20"),
    cashActive: yup.boolean(),
    surPlace: yup
      .string()
      .required("Le label est requis")
      .max(20, "Le label doit être de longueur maximale 20"),
    surPlaceActive: yup.boolean(),
    importer: yup
      .string()
      .required("Le label est requis")
      .max(20, "Le label doit être de longueur maximale 20"),
    importerActive: yup.boolean(),
    carouselDuration: yup.number().min(1, "La durée minimal est 1"),
    carouselTiming: yup.number().min(1, "La durée minimal est 1"),
    defaultLanguage: yup.string().oneOf(["fr", "en", "ar"]).required(),
    loyaltyEarnPoints: yup
      .number()
      .typeError(t("auth.required"))
      .required(t("auth.required"))
      .integer(t("auth.required"))
      .min(1, t("auth.required")),
    loyaltyRedeemPoints: yup
      .number()
      .typeError(t("auth.required"))
      .required(t("auth.required"))
      .integer(t("auth.required"))
      .min(1, t("auth.required")),
    loyaltyMaxPercent: yup
      .number()
      .typeError(t("auth.required"))
      .required(t("auth.required"))
      .integer(t("auth.required"))
      .min(1, t("auth.required"))
      .max(100, t("auth.required")),
  });

  const initialValues = {
    qrCode: data?.qrCode || "",
    tva: data?.tva || 0,
    // maxDrink: data?.maxDrink || 1,
    // maxDessert: data?.maxDessert || 1,
    // maxExtras: data?.maxExtras || 1,
    card: data?.method?.[0]?.label || "Carte",
    cardId: data?.method?.[0]?._id || "",
    cardActive: data?.method?.[0]?.isActive ?? true,
    cardMinAmount: data?.cardMinAmount ?? 10,
    cardMinMessageFr: data?.cardMinMessage?.fr || defaultCardMinMessage.fr,
    cardMinMessageEn: data?.cardMinMessage?.en || defaultCardMinMessage.en,
    cardMinMessageAr: data?.cardMinMessage?.ar || defaultCardMinMessage.ar,
    loyaltyEnabled: data?.loyaltyEnabled ?? false,
    cash: data?.method?.[1]?.label || "Espèces",
    cashId: data?.method?.[1]?._id || "",
    cashActive: data?.method?.[1]?.isActive ?? true,
    surPlace: data?.pack?.[0]?.label || "Sur Place",
    surPlaceId: data?.pack?.[0]?._id || "",
    surPlaceActive: data?.pack?.[0]?.isActive ?? true,
    importer: data?.pack?.[1]?.label || "À Emporter",
    importerId: data?.pack?.[1]?._id || "",
    importerActive: data?.pack?.[1]?.isActive ?? true,
    carouselDuration: data?.carouselDuration || 5,
    carouselTiming: data?.carouselTiming || 120,
    defaultLanguage: data?.defaultLanguage || "fr",
    loyaltyEarnPoints: data?.loyaltyEarnPoints ?? 1,
    loyaltyRedeemPoints: data?.loyaltyRedeemPoints ?? 100,
    loyaltyMaxPercent: data?.loyaltyMaxPercent ?? 50,
  };
  
  const handleFormSubmit = (values) => {
    const formData = new FormData();
    formData.append("tva", values.tva);
    formData.append("qrCode", values.qrCode);
    // formData.append("maxExtras", values.maxExtras);
    // formData.append("maxDessert", values.maxDessert);
    // formData.append("maxDrink", values.maxDrink);
    formData.append("carouselDuration", values.carouselDuration);
    formData.append("carouselTiming", values.carouselTiming);
    formData.append("defaultLanguage", values.defaultLanguage || "fr");
    formData.append(
      "loyaltyEnabled",
      values.loyaltyEnabled === true || values.loyaltyEnabled === "true" ? "true" : "false"
    );
    formData.append("loyaltyEarnPoints", values.loyaltyEarnPoints);
    formData.append("loyaltyRedeemPoints", values.loyaltyRedeemPoints);
    formData.append("loyaltyMaxPercent", values.loyaltyMaxPercent);
    formData.append("cardMinAmount", values.cardMinAmount);
    formData.append(
      "cardMinMessage",
      JSON.stringify({
        fr: values.cardMinMessageFr,
        en: values.cardMinMessageEn,
        ar: values.cardMinMessageAr,
      })
    );

    const methods = [
      { label: values.card, isActive: values.cardActive, _id: values.cardId },
      { label: values.cash, isActive: values.cashActive, _id: values.cashId },
    ];
    const packs = [
      {
        label: values.surPlace,
        isActive: values.surPlaceActive,
        _id: values.surPlaceId,
      },
      {
        label: values.importer,
        isActive: values.importerActive,
        _id: values.importerId,
      },
    ];

    formData.append("method", JSON.stringify(methods));
    formData.append("pack", JSON.stringify(packs));
    if (bannerPreview) {
      appendImageFormFields(formData, bannerPreview, "banner");
    }
    dispatch(updateSetting({formData, restaurantId}));
  };

  useEffect(() => {
    if (status === "updateSettingSuccess") {
      toast.success(success);
      dispatch(updateStatus());
      navigate("/settings");
    } else if (status === "updateSettingError") {
      toast.error(error);
    }
  }, [status, error, dispatch, navigate, success]);

  return (
    <Box m="20px" className="main-application">
      <BackButton/>
      <Header
        title={t("headers.editSettings")}
        subtitle={t("headers.editSettingsSub")}
      />
      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={settingsSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="16px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 6" },
              }}
            >
              <TextFieldCompnent
                type="text"
                label={t("fields.qrLink")}
                change={handleChange}
                value={values.qrCode}
                name="qrCode"
                blur={handleBlur}
                touched={touched.qrCode}
                error={errors.qrCode}
                colum="span 2"
                required
              />
              <TextFieldCompnent
                type="number"
                label={t("fields.tva")}
                change={handleChange}
                value={values.tva}
                name="tva"
                blur={handleBlur}
                touched={touched.tva}
                error={errors.tva}
                colum="span 2"
                num={0}
                required
              />
              {/* <TextFieldCompnent
                type="number"
                label={t("fields.maxExtras")}
                change={handleChange}
                value={values.maxExtras}
                name="maxExtras"
                blur={handleBlur}
                touched={touched.maxExtras}
                error={errors.maxExtras}
                colum="span 2"
                num={1}
                onlyDigits={true}
              />
              <TextFieldCompnent
                type="number"
                label={t("fields.maxDrinks")}
                change={handleChange}
                value={values.maxDrink}
                name="maxDrink"
                blur={handleBlur}
                touched={touched.maxDrink}
                error={errors.maxDrink}
                colum="span 2"
                num={1}
                onlyDigits={true}
              />
              <TextFieldCompnent
                type="number"
                label={t("fields.maxDesserts")}
                change={handleChange}
                value={values.maxDessert}
                name="maxDessert"
                blur={handleBlur}
                touched={touched.maxDessert}
                error={errors.maxDessert}
                colum="span 2"
                num={1}
                onlyDigits={true}
              /> */}
              <FormControl
                variant="filled"
                fullWidth
                sx={{ gridColumn: "span 2" }}
              >
                <FormLabel required>{t("fields.defaultLanguage")}</FormLabel>
                <RadioGroup
                  name="defaultLanguage"
                  value={values.defaultLanguage}
                  onChange={handleChange}
                  row
                >
                  <FormControlLabel
                    value="fr"
                    control={<Radio />}
                    label={t("language.fr")}
                  />
                  <FormControlLabel
                    value="en"
                    control={<Radio />}
                    label={t("language.en")}
                  />
                  <FormControlLabel
                    value="ar"
                    control={<Radio />}
                    label={t("language.ar")}
                  />
                </RadioGroup>
              </FormControl>
              <FormControl
                variant="filled"
                fullWidth
                sx={{ gridColumn: "span 2" }}
              >
                <FormLabel>{t("fields.loyaltyEnabled")}</FormLabel>
                <RadioGroup
                  name="loyaltyEnabled"
                  value={values.loyaltyEnabled}
                  onChange={handleChange}
                  row
                >
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label={t("common.no")}
                  />
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label={t("common.yes")}
                  />
                </RadioGroup>
              </FormControl>
              <TextFieldCompnent
                type="number"
                label={t("fields.loyaltyEarnPoints")}
                change={handleChange}
                value={values.loyaltyEarnPoints}
                name="loyaltyEarnPoints"
                blur={handleBlur}
                touched={touched.loyaltyEarnPoints}
                error={errors.loyaltyEarnPoints}
                colum="span 2"
                num={1}
                onlyDigits={true}
                required
              />
              <TextFieldCompnent
                type="number"
                label={t("fields.loyaltyRedeemPoints")}
                change={handleChange}
                value={values.loyaltyRedeemPoints}
                name="loyaltyRedeemPoints"
                blur={handleBlur}
                touched={touched.loyaltyRedeemPoints}
                error={errors.loyaltyRedeemPoints}
                colum="span 2"
                num={1}
                onlyDigits={true}
                required
              />
              <TextFieldCompnent
                type="number"
                label={t("fields.loyaltyMaxPercent")}
                change={handleChange}
                value={values.loyaltyMaxPercent}
                name="loyaltyMaxPercent"
                blur={handleBlur}
                touched={touched.loyaltyMaxPercent}
                error={errors.loyaltyMaxPercent}
                colum="span 2"
                num={1}
                onlyDigits={true}
                required
              />
              <TextFieldCompnent
                type="number"
                label={t("fields.carouselDuration")}
                change={handleChange}
                value={values.carouselDuration}
                name="carouselDuration"
                blur={handleBlur}
                touched={touched.carouselDuration}
                error={errors.carouselDuration}
                colum="span 2"
                num={1}
                onlyDigits={true}
              />
              <TextFieldCompnent
                type="number"
                label={t("fields.carouselIdle")}
                change={handleChange}
                value={values.carouselTiming}
                name="carouselTiming"
                blur={handleBlur}
                touched={touched.carouselTiming}
                error={errors.carouselTiming}
                colum="span 2"
                num={1}
                onlyDigits={true}
              />
              <TextField
                label={`${t("fields.cardMinMessage")} (fr)`}
                name="cardMinMessageFr"
                value={values.cardMinMessageFr}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.cardMinMessageFr && errors.cardMinMessageFr)}
                helperText={t("fields.cardMinMessageHint")}
                fullWidth
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                label={`${t("fields.cardMinMessage")} (en)`}
                name="cardMinMessageEn"
                value={values.cardMinMessageEn}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.cardMinMessageEn && errors.cardMinMessageEn)}
                helperText={t("fields.cardMinMessageHint")}
                fullWidth
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                label={`${t("fields.cardMinMessage")} (ar)`}
                name="cardMinMessageAr"
                value={values.cardMinMessageAr}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.cardMinMessageAr && errors.cardMinMessageAr)}
                helperText={t("fields.cardMinMessageHint")}
                fullWidth
                sx={{ gridColumn: "span 2" }}
              />
              <TextFieldCompnent
                type="number"
                label={t("fields.cardMinAmount")}
                change={handleChange}
                value={values.cardMinAmount}
                name="cardMinAmount"
                blur={handleBlur}
                touched={touched.cardMinAmount}
                error={errors.cardMinAmount}
                colum="span 2"
                num={0}
                required
              />
              <TextFieldCompnent
                type="text"
                label={t("fields.cardPaymentLabel")}
                change={handleChange}
                value={values.card}
                name="card"
                blur={handleBlur}
                touched={touched.card}
                error={errors.card}
                colum="span 2"
                required
              />
              <FormControl
                variant="filled"
                fullWidth
                sx={{ gridColumn: "span 2" }}
              >
                <FormLabel>{t("fields.acceptCard")}</FormLabel>
                <RadioGroup
                  name="cardActive"
                  value={values.cardActive}
                  onChange={handleChange}
                  row
                >
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label={t("common.no")}
                  />
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label={t("common.yes")}
                  />
                </RadioGroup>
              </FormControl>
              <TextFieldCompnent
                type="text"
                label={t("fields.cashPaymentLabel")}
                change={handleChange}
                value={values.cash}
                name="cash"
                blur={handleBlur}
                touched={touched.cash}
                error={errors.cash}
                colum="span 2"
                required
              />
              <FormControl
                variant="filled"
                fullWidth
                sx={{ gridColumn: "span 2" }}
              >
                <FormLabel>{t("fields.acceptCash")}</FormLabel>
                <RadioGroup
                  name="cashActive"
                  value={values.cashActive}
                  onChange={handleChange}
                  row
                >
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label={t("common.no")}
                  />
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label={t("common.yes")}
                  />
                </RadioGroup>
              </FormControl>
              <TextFieldCompnent
                type="text"
                label={t("fields.dineInMode")}
                change={handleChange}
                value={values.surPlace}
                name="surPlace"
                blur={handleBlur}
                touched={touched.surPlace}
                error={errors.surPlace}
                colum="span 2"
                required
              />
              <FormControl
                variant="filled"
                fullWidth
                sx={{ gridColumn: "span 2" }}
              >
                <FormLabel>{t("fields.acceptDineIn")}</FormLabel>
                <RadioGroup
                  name="surPlaceActive"
                  value={values.surPlaceActive}
                  onChange={handleChange}
                  row
                >
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label={t("common.no")}
                  />
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label={t("common.yes")}
                  />
                </RadioGroup>
              </FormControl>
              <TextFieldCompnent
                type="text"
                label={t("fields.takeawayMode")}
                change={handleChange}
                value={values.importer}
                name="importer"
                blur={handleBlur}
                touched={touched.importer}
                error={errors.importer}
                colum="span 2"
                required
              />
              <FormControl
                variant="filled"
                fullWidth
                sx={{ gridColumn: "span 2" }}
              >
                <FormLabel>{t("fields.acceptTakeaway")}</FormLabel>
                <RadioGroup
                  name="importerActive"
                  value={values.importerActive}
                  onChange={handleChange}
                  row
                >
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label={t("common.no")}
                  />
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label={t("common.yes")}
                  />
                </RadioGroup>
              </FormControl>
              <Box sx={{ gridColumn: "span 4" }}>
                <Typography variant="h2" color="inherit">
                    {t("fields.banner")}
                </Typography>
                <br />
                <ImageInput
                  inputId="bannerInput"
                  previewImage={bannerPreview}
                  setPreviewImage={setBannerPreview}
                  image={data.banner}
                  mediaTargetType="Settings"
                />
              </Box>
            </Box>
            <Box display="flex" justifyContent="start" mt="20px">
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={loading}
              >
                {loading ? <Loading size={8} /> : t("common.edit")}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default ModifySettings;
